import { useEffect } from "react";
import { api } from "../api/client.js";
import { useOptionalWebSocketContext } from "../contexts/WebSocketContext.jsx";
import { loadResource } from "../lib/resourceCache.js";
import { FLEET_KEYS } from "../lib/resourceKeys.js";
import { useResource } from "./useResource.js";

/**
 * Recarga en curso disparada por el socket, viva SOLO durante el despacho del
 * mensaje. Los cuatro consumidores reciben el mismo `pending_device_new` en el
 * mismo bucle síncrono del provider, así que el primero pide y los demás se
 * cuelgan de esa recarga; la marca se suelta en el microtask siguiente para que
 * el próximo evento sí vuelva a pedir. No guarda datos de nadie —es un
 * manejador de promesa que se limpia solo—, así que no es el estado de módulo
 * contra el que argumenta el encabezado del hook, acá abajo.
 */
let recargaDelDespacho = null;

function recargarPendientes() {
  if (recargaDelDespacho) return recargaDelDespacho;
  // `force`: la carga que pueda haber en vuelo se lanzó ANTES de este evento,
  // así que su respuesta no incluye al equipo que lo provocó. Sin `force`,
  // `loadResource` la reutilizaría y el badge se quedaría con el número viejo
  // hasta el próximo montaje — justo lo que pasa cuando llegan dos equipos
  // seguidos, o cuando el primero llega mientras corre la carga inicial.
  recargaDelDespacho = loadResource(
    FLEET_KEYS.pendingDevices,
    api.getPendingDevices,
    { force: true },
  ).catch(() => {
    /* el badge simplemente no cambia */
  });
  // Microtask con `Promise.resolve()` y no `queueMicrotask`: el segundo no
  // está en los globals del eslint del repo.
  Promise.resolve().then(() => {
    recargaDelDespacho = null;
  });
  return recargaDelDespacho;
}

/**
 * Conteo de dispositivos pendientes (sin revisar) para los badges de navegación.
 *
 * ── Por qué el número sale de `resourceCache` y no de un `useState` ─────────
 * Este hook se monta CUATRO veces en la misma pantalla: <TopNav/>,
 * <BottomNav/> y <AdminNavTree/> dos veces — el rail de desktop y el menú de
 * móvil se tapan por CSS, no por desmontaje (ver el encabezado de
 * AdminNavTree). Con un contador por instancia eso eran cuatro
 * `GET /api/fleet/pending-devices` idénticos en cada carga de /admin, y cuatro
 * estados que podían DIVERGIR: el `.catch()` es silencioso, así que la
 * instancia cuya petición fallaba se quedaba en 0 mientras las otras mostraban
 * el número.
 *
 * `resourceCache` ya resuelve las cuatro cosas que hacían falta —una carga en
 * vuelo compartida, un solo valor para todos, el error guardado en la entrada
 * y no en cada consumidor— y encima la clave es la MISMA que lee la cola de
 * pendientes (`PendingTab` en DispositivosPage) y que calienta la precarga del
 * rail: el badge deja de pagar una petición que la app ya estaba haciendo.
 *
 * ── Por qué no un store propio con `useSyncExternalStore` ──────────────────
 * Ése es el patrón de `useAdminNavSections`, y ahí es el correcto: qué
 * secciones están desplegadas es estado de UI, sin dueño en el servidor. Esto
 * es un dato del servidor, y un store de módulo lo dejaría FUERA de los tres
 * sitios donde `AuthContext` vacía la caché (logout, cambio de usuario en la
 * misma pestaña, 401). O sea: reabriría en pequeño el agujero que cerró el
 * review de seguridad —el dato de un usuario visible para el siguiente en la
 * misma pestaña— y sería una SEGUNDA fuente de verdad sobre la misma lista que
 * ya cachea la cola de pendientes, con la que podría discrepar.
 *
 * @returns {number} dispositivos en estado `pending` (el endpoint devuelve
 *   también los rechazados, que no cuentan para el badge).
 */
export function usePendingCount() {
  const { data } = useResource(
    FLEET_KEYS.pendingDevices,
    api.getPendingDevices,
    { fallback: [] },
  );
  const ctx = useOptionalWebSocketContext();

  // Suscripción OPCIONAL al socket: un contador de badge no puede tumbar la
  // pantalla, ni obligar a montar el <WebSocketProvider/> a quien renderice
  // estos componentes sueltos. Sin provider el número igual llega por la carga
  // inicial, solo que no se refresca en vivo.
  useEffect(() => {
    if (!ctx) return undefined;
    const handler = (msg) => {
      if (msg.type !== "pending_device_new") return;
      // El evento trae `pending_count`, pero acá se usa solo como SEÑAL: el
      // número se recalcula de la lista, que es la que miran también la cola de
      // pendientes y la precarga. Aplicar el `pending_count` a mano crearía
      // otra vez dos verdades — el badge en 3 y la cola con 2 filas.
      recargarPendientes();
    };
    ctx.subscribe(handler);
    return () => ctx.unsubscribe(handler);
  }, [ctx]);

  // `Array.isArray` y no `data.filter` a secas: este badge vive en el shell, no
  // en una ruta. Un cuerpo inesperado (un 200 con objeto de error) sería un
  // throw EN RENDER que se lleva por delante la app entera en vez de dejar un
  // badge en 0. Antes el `.filter` iba dentro del `.then` y lo absorbía el
  // `.catch()`.
  return Array.isArray(data)
    ? data.filter((d) => d.status === "pending").length
    : 0;
}
